import { AlertTriangle, Users2, HeartPulse, Dumbbell, ClipboardList } from "lucide-react";
import { SPORT_RISK } from "@/lib/biohackerBrand";

/**
 * <WorkloadRiskModule sportId="basketball" />
 *
 * Sport-agnostic "Workload & Structural Risk Analysis" block dropped
 * into every Sport Intelligence Package page (flagship Baseball page
 * and the shared placeholder for the other seven sports).
 *
 * Renders in two acts:
 *   1. The physics baseline — sport-specific kinetic breakdown, styled
 *      as an alert so it reads as an unmistakable "here's the actual
 *      cost of this sport" statement.
 *   2. The four-stakeholder impact grid — AD / AT / SCSA / Coach.
 *      Each card shows a Risk row and a Solution row so the reader
 *      immediately sees the operational significance to their role.
 */
const STAKEHOLDER_META = [
  { key: "ad",    label: "Athletic Directors",      subtitle: "Protecting the bottom line + mitigating liability",       icon: ClipboardList, tone: "#F59E0B" },
  { key: "at",    label: "Athletic Trainers",       subtitle: "Managing micro-trauma + preventing season-ending tears",  icon: HeartPulse,    tone: "#F43F5E" },
  { key: "scsa",  label: "Strength & Conditioning / Sports Science", subtitle: "Quantifying force + building structural resilience", icon: Dumbbell,      tone: "#22D3EE" },
  { key: "coach", label: "Coaching Staff",          subtitle: "Maximizing availability + competitive readiness",         tone: "#A78BFA",     icon: Users2 },
];

function renderPhysicsBody(body) {
  // Very small **bold** parser — treats `**text**` as inline emphasis.
  const parts = String(body).split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    if (/^\*\*[^*]+\*\*$/.test(p)) {
      return <span key={i} className="text-white font-bold whitespace-nowrap">{p.replace(/\*\*/g, "")}</span>;
    }
    return <span key={i}>{p}</span>;
  });
}

export default function WorkloadRiskModule({ sportId }) {
  const data = SPORT_RISK[sportId];
  if (!data) return null;

  return (
    <section
      className="py-16 border-t border-white/8 bg-gradient-to-b from-black/40 to-[#050810]"
      data-testid={`sport-risk-${sportId}`}
    >
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-[10px] tracking-[0.4em] uppercase font-bold text-fuchsia-300/80 mb-3 flex items-center gap-2">
          <AlertTriangle className="h-3.5 w-3.5" />
          Workload & Structural Risk Analysis
        </div>
        <h2 className="font-display font-black text-3xl sm:text-4xl text-white tracking-tight leading-[1.1]">
          The physics your program is already paying for.<br />
          <span className="text-white/50">Now measured, dashboarded, and defensible.</span>
        </h2>

        {/* Physics baseline */}
        <div
          className="mt-8 rounded-2xl p-6 border"
          style={{ borderColor: "rgba(217,4,41,0.35)", backgroundColor: "rgba(217,4,41,0.06)" }}
          data-testid={`sport-risk-physics-${sportId}`}
        >
          <div className="text-[10px] uppercase tracking-[0.3em] text-red-300 font-bold flex items-center gap-2">
            <AlertTriangle className="h-3 w-3" /> Physics baseline
          </div>
          <div className="mt-2 font-display font-black text-white text-xl leading-tight">
            {data.physics.headline}
          </div>
          <p className="mt-3 text-white/80 leading-relaxed">
            {renderPhysicsBody(data.physics.body)}
          </p>
        </div>

        {/* Four-stakeholder grid */}
        <div className="mt-8 grid md:grid-cols-2 gap-4" data-testid={`sport-risk-stakeholders-${sportId}`}>
          {STAKEHOLDER_META.map((meta) => {
            const s = data.stakeholders?.[meta.key];
            if (!s) return null;
            const Icon = meta.icon;
            return (
              <div
                key={meta.key}
                className="ink-card rounded-2xl p-6"
                data-testid={`sport-risk-role-${sportId}-${meta.key}`}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center"
                       style={{ backgroundColor: `${meta.tone}22`, color: meta.tone }}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="font-display font-black text-white text-lg leading-tight">{meta.label}</div>
                    <div className="text-[11px] text-white/50">{meta.subtitle}</div>
                  </div>
                </div>
                <div className="space-y-3 text-sm">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-red-300/80 font-bold mb-1">The risk</div>
                    <div className="text-white/80 leading-relaxed">{s.risk}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-emerald-300/80 font-bold mb-1">The solution</div>
                    <div className="text-white/80 leading-relaxed">{s.solution}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
